import { useContext, useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import { Loader, usePagination, ButtonModifier } from "../../../components";
import {
  ShowTableAngsuranKelompok,
  ShowTableMisalLelangKelompok,
  ShowTableHitunganSimulasi,
  ShowTablePesertaKelompok,
} from "../../../components/ShowTable";
import { postRequest } from "../../../constants/api";
import { Container, Card, Form, Row, Col } from "react-bootstrap";
import { Box, ButtonGroup, Button, Pagination } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import useScreenWidth from "../../../hooks/useScreenWidth";
import { downloadExcel } from "react-export-table-to-excel";
import { formatDate } from "../../../constants/helper";

const toNumber = (value) => {
  if (value === null || value === undefined || value === "" || value === ".")
    return 0;

  if (typeof value === "number") {
    if (Number.isNaN(value)) return 0;
    return value;
  }

  let cleaned = String(value).trim();
  if (cleaned === "") return 0;

  cleaned = cleaned.replace(/\s/g, "");

  if (cleaned.includes(",") && cleaned.includes(".")) {
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (cleaned.includes(".") && !cleaned.includes(",")) {
    const parts = cleaned.split(".");
    const lastPart = parts[parts.length - 1];

    if (lastPart.length === 3 && parts.length > 1) {
      cleaned = cleaned.replace(/\./g, "");
    }
  } else if (cleaned.includes(",") && !cleaned.includes(".")) {
    cleaned = cleaned.replace(",", ".");
  }

  const num = Number(cleaned);
  return Number.isNaN(num) ? 0 : num;
};

const roundInteger = (value) => {
  if (value === null || value === undefined || value === "" || value === ".")
    return value;

  const num = Number(value);
  if (Number.isNaN(num)) return value;

  return Math.round(num);
};

const roundObjectNumbers = (obj) => {
  const result = {};

  Object.keys(obj).forEach((key) => {
    const value = obj[key];

    if (typeof value === "number" && Number.isFinite(value)) {
      result[key] = Math.round(value);
    } else {
      result[key] = value;
    }
  });

  return result;
};

const ceilTo = (value, step) => {
  const num = toNumber(value);
  if (!num) return 0;
  return Math.ceil(num / step) * step;
};

const floorTo = (value, step) => {
  const num = toNumber(value);
  if (!num) return 0;
  return Math.floor(num / step) * step;
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (value === ".") return ".";

  const rounded = roundInteger(value);
  const num = Number(rounded);

  if (Number.isNaN(num)) return value;

  return num.toLocaleString("de-DE");
};

const formatPercent = (value, decimal = 2) => {
  if (value === null || value === undefined || value === "") return "";
  if (value === ".") return ".";

  const num = Number(value);
  if (Number.isNaN(num)) return value;

  return `${(num * 100).toFixed(decimal)}%`;
};

const formatBungaPercent = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (value === ".") return ".";

  const num = Number(value);
  if (Number.isNaN(num)) return value;

  return `${Math.round(num * 100)}%`;
};

const getPendapatanAdm = ({ periode, patokanHarga }) => {
  const periodeNum = toNumber(periode);
  const patokanHargaNum = toNumber(patokanHarga);

  if (!patokanHargaNum) return 0;

  if (periodeNum <= 13) {
    return patokanHargaNum * 0.04;
  }

  if (periodeNum <= 21) {
    return patokanHargaNum * 0.0325;
  }

  return patokanHargaNum * 0.026;
};

const hitungAngsuranSimulasi = ({
  periode,
  nominalLelang,
  subsidi,
  patokanHarga,
  pendapatanAdm,
  jangkaWaktu,
  angsuranDasar,
}) => {
  const periodeNum = toNumber(periode);
  const nominalLelangNum = toNumber(nominalLelang);
  const subsidiNum = toNumber(subsidi);
  const patokanHargaNum = toNumber(patokanHarga);
  const pendapatanAdmNum = toNumber(pendapatanAdm);
  const jangkaWaktuNum = toNumber(jangkaWaktu) || 36;
  const angsuranDasarNum = toNumber(angsuranDasar);

  if (periodeNum === 1) {
    return angsuranDasarNum;
  }

  const rawAngsuran =
    (patokanHargaNum + pendapatanAdmNum - nominalLelangNum - subsidiNum) /
    jangkaWaktuNum;

  let hasilAngsuran = 0;

  if (periodeNum >= 2 && periodeNum <= 15) {
    hasilAngsuran = ceilTo(rawAngsuran, 100);
  } else {
    hasilAngsuran = ceilTo(rawAngsuran, 1000);
  }

  const batasMaksimalAngsuran = angsuranDasarNum * 0.9;

  if (batasMaksimalAngsuran > 0 && hasilAngsuran > batasMaksimalAngsuran) {
    hasilAngsuran = batasMaksimalAngsuran;
  }

  return hasilAngsuran;
};

const hitungTotalPenerimaanLelang = ({
  periode,
  nominalLelang,
  patokanHarga,
  pendapatanAdm,
  angsuran,
  angsuranDasar,
  subsidi,
  jangkaWaktu,
}) => {
  const periodeNum = toNumber(periode);
  const nominalLelangNum = toNumber(nominalLelang);
  const patokanHargaNum = toNumber(patokanHarga);
  const pendapatanAdmNum = toNumber(pendapatanAdm);
  const angsuranNum = toNumber(angsuran);
  const angsuranDasarNum = toNumber(angsuranDasar);
  const subsidiNum = toNumber(subsidi);
  const jangkaWaktuNum = toNumber(jangkaWaktu) || 36;

  if (periodeNum === 1) {
    return patokanHargaNum - nominalLelangNum + pendapatanAdmNum;
  }

  if (periodeNum === 2) {
    return patokanHargaNum - nominalLelangNum;
  }

  const rawAngsuran =
    (patokanHargaNum + pendapatanAdmNum - nominalLelangNum - subsidiNum) /
    jangkaWaktuNum;

  const batasMaksimalAngsuran = angsuranDasarNum * 0.9;
  const rawAngsuranDipakai =
    batasMaksimalAngsuran > 0 && rawAngsuran > batasMaksimalAngsuran
      ? batasMaksimalAngsuran
      : rawAngsuran;

  if (periodeNum >= 3 && periodeNum <= 15) {
    return ceilTo(
      patokanHargaNum -
        nominalLelangNum +
        (angsuranDasarNum - rawAngsuranDipakai) * (periodeNum - 2),
      100,
    );
  }

  const hasil =
    patokanHargaNum -
    nominalLelangNum +
    (angsuranDasarNum - angsuranNum) * (periodeNum - 2);

  // Di Excel asli ada kasus nominal .500 yang jatuh ke ribuan bawah.
  if (hasil % 1000 === 500) {
    return floorTo(hasil, 1000);
  }

  return hasil;
};

const buildHitunganSimulasiFrontend = ({
  hitunganSimulasisData,
  patokanHarga,
  angsuran,
  jangkaWaktu,
}) => {
  const data = hitunganSimulasisData || [];

  const patokanHargaNum = toNumber(patokanHarga);
  const angsuranDasarNum = toNumber(angsuran);
  const jangkaWaktuNum = toNumber(jangkaWaktu) || 36;

  let runningYangSudahDisetor = 0;

  return data.map((item, index) => {
    const periode = index + 1;

    const nominalLelang = toNumber(item.nominalLelang);
    const subsidi = toNumber(item.subsidi);
    const cadangan = item.cadangan === "" ? "" : toNumber(item.cadangan);
    const telatBulan = toNumber(item.telatBulan);

    const finalPatokanHarga = patokanHargaNum || toNumber(item.patokanHarga);

    const pendapatanAdmHitung = getPendapatanAdm({
      periode,
      patokanHarga: finalPatokanHarga,
    });

    const angsuranFinal = hitungAngsuranSimulasi({
      periode,
      nominalLelang,
      subsidi,
      patokanHarga: finalPatokanHarga,
      pendapatanAdm: pendapatanAdmHitung,
      jangkaWaktu: jangkaWaktuNum,
      angsuranDasar: angsuranDasarNum,
    });

    if (periode === 1) {
      runningYangSudahDisetor = 0;
    } else {
      runningYangSudahDisetor += angsuranFinal;
    }

    const sisaPeriode = jangkaWaktuNum - periode;
    const hutang = sisaPeriode * angsuranDasarNum;

    const yangSudahDisetor =
      periode === 1 ? "" : angsuranDasarNum + runningYangSudahDisetor;

    const totalSetor =
      periode === 1 ? hutang : hutang + toNumber(yangSudahDisetor);

    const totalPenerimaanLelang = hitungTotalPenerimaanLelang({
      periode,
      nominalLelang,
      patokanHarga: finalPatokanHarga,
      pendapatanAdm: pendapatanAdmHitung,
      angsuran: angsuranFinal,
      angsuranDasar: angsuranDasarNum,
      subsidi,
      jangkaWaktu: jangkaWaktuNum,
    });

    const uangYangDiterima = periode === 1 ? hutang : totalPenerimaanLelang;

    const selisih =
      periode === 1 ? "" : totalSetor - toNumber(uangYangDiterima);

    const bunga =
      periode === 1
        ? 0
        : uangYangDiterima !== 0
          ? toNumber(selisih) / uangYangDiterima
          : 0;

    const nominalTerlambat = telatBulan * angsuranDasarNum;

    return {
      ...item,

      nominalLelang,
      subsidi,
      angsuran: angsuranFinal,
      cadangan,
      totalPenerimaanLelang,

      lelangUndi: item.lelangUndi,

      sisaPeriode,
      hutang,
      yangSudahDisetor,
      totalSetor,
      uangYangDiterima,
      selisih,
      bunga,

      telatBulan,
      nominalTerlambat,

      adm1: periode === 1 ? "" : 0.04,
      adm2: periode === 1 ? "" : 0.0325,
      adm3: periode === 1 ? "" : 0.026,
      patokanHarga: periode === 1 ? "" : finalPatokanHarga,
      pendapatanAdm: periode === 1 ? "" : pendapatanAdmHitung,
    };
  });
};

const TampilKelompok = () => {
  const screenSize = useScreenWidth();
  const { user, dispatch } = useContext(AuthContext);

  const [noKelompok, setNoBukti] = useState("");
  const [tglKelompok, setTglKelompok] = useState("");
  const [patokanHarga, setPatokanHarga] = useState("");
  const [angsuran, setAngsuran] = useState("");
  const [totalAngsuran, setTotalAngsuran] = useState("");
  const [jangkaWaktu, setJangkaWaktu] = useState("");
  const [administrasi, setAdministrasi] = useState("");
  const [patokanBayangan, setPatokanBayangan] = useState("");
  const [keterangan, setKeterangan] = useState("");
  const [totalBiaya, setTotalBiaya] = useState("");
  const [isPost, setIsPost] = useState("");

  const [userPesertaKelompoksData, setUserPesertaKelompoksData] = useState([]);
  const [pesertaKelompoksData, setPesertaKelompoksData] = useState([]);
  const [angsuranKelompoksData, setAngsuranKelompoksData] = useState([]);
  const [misalLelangKelompoksData, setMisalLelangKelompoksData] = useState([]);
  const [hitunganSimulasisData, setHitunganSimulasisData] = useState([]);

  const [previewExcel, setPreviewExcel] = useState(false);
  const [previewExcelSimulasiLimitLelang, setPreviewExcelSimulasiLimitLelang] =
    useState(false);
  const [previewExcelHitungSimulasi, setPreviewExcelHitungSimulasi] =
    useState(false);

  const [simulasiLimitLelang, setSimulasiLimitLelang] = useState([]);

  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  let isEditableManager = user.tipeUser !== "ADMIN" && isPost === false;
  let isEditableAdmin = true;
  let isEditable = isEditableManager || isEditableAdmin;

  const calculatedHitunganSimulasisData = useMemo(() => {
    return buildHitunganSimulasiFrontend({
      hitunganSimulasisData,
      patokanHarga,
      angsuran,
      jangkaWaktu,
    });
  }, [hitunganSimulasisData, patokanHarga, angsuran, jangkaWaktu]);

  let [page, setPage] = useState(1);
  const PER_PAGE = 20;

  let [pageAngsuranKelompok, setPageAngsuranKelompok] = useState(1);
  const PER_PAGE_AngsuranKelompok = 20;

  let [pageMisalLelangKelompok, setPageMisalLelangKelompok] = useState(1);
  const PER_PAGE_MisalLelangKelompok = 20;

  let [pageHitunganSimulasi, setPageHitunganSimulasi] = useState(1);
  const PER_PAGE_HitunganSimulasi = 20;

  const indexOfLastPost = page * PER_PAGE;
  const indexOfFirstPost = indexOfLastPost - PER_PAGE;
  const currentPosts = pesertaKelompoksData.slice(
    indexOfFirstPost,
    indexOfLastPost,
  );

  const count = Math.ceil(pesertaKelompoksData.length / PER_PAGE);
  const _DATA = usePagination(pesertaKelompoksData, PER_PAGE);

  const handleChange = (e, p) => {
    setPage(p);
    _DATA.jump(p);
  };

  const indexOfLastPostAngsuranKelompok =
    pageAngsuranKelompok * PER_PAGE_AngsuranKelompok;
  const indexOfFirstPostAngsuranKelompok =
    indexOfLastPostAngsuranKelompok - PER_PAGE_AngsuranKelompok;
  const currentPostsAngsuranKelompok = angsuranKelompoksData.slice(
    indexOfFirstPostAngsuranKelompok,
    indexOfLastPostAngsuranKelompok,
  );

  const countAngsuranKelompok = Math.ceil(
    angsuranKelompoksData.length / PER_PAGE,
  );
  const _DATA_AngsuranKelompok = usePagination(angsuranKelompoksData, PER_PAGE);

  const handleChangeAngsuranKelompok = (e, p) => {
    setPageAngsuranKelompok(p);
    _DATA_AngsuranKelompok.jump(p);
  };

  const indexOfLastPostMisalLelangKelompok =
    pageMisalLelangKelompok * PER_PAGE_MisalLelangKelompok;
  const indexOfFirstPostMisalLelangKelompok =
    indexOfLastPostMisalLelangKelompok - PER_PAGE_MisalLelangKelompok;
  const currentPostsMisalLelangKelompok = misalLelangKelompoksData.slice(
    indexOfFirstPostMisalLelangKelompok,
    indexOfLastPostMisalLelangKelompok,
  );

  const countMisalLelangKelompok = Math.ceil(
    misalLelangKelompoksData.length / PER_PAGE,
  );
  const _DATA_MisalLelangKelompok = usePagination(
    misalLelangKelompoksData,
    PER_PAGE,
  );

  const handleChangeMisalLelangKelompok = (e, p) => {
    setPageMisalLelangKelompok(p);
    _DATA_MisalLelangKelompok.jump(p);
  };

  const indexOfLastPostHitunganSimulasi =
    pageHitunganSimulasi * PER_PAGE_HitunganSimulasi;
  const indexOfFirstPostHitunganSimulasi =
    indexOfLastPostHitunganSimulasi - PER_PAGE_HitunganSimulasi;
  const currentPostsHitunganSimulasi = calculatedHitunganSimulasisData.slice(
    indexOfFirstPostHitunganSimulasi,
    indexOfLastPostHitunganSimulasi,
  );

  const countHitunganSimulasi = Math.ceil(
    calculatedHitunganSimulasisData.length / PER_PAGE,
  );
  const _DATA_HitunganSimulasi = usePagination(
    calculatedHitunganSimulasisData,
    PER_PAGE,
  );

  const handleChangeHitunganSimulasi = (e, p) => {
    setPageHitunganSimulasi(p);
    _DATA_HitunganSimulasi.jump(p);
  };

  const getSimulasiLimitLelang = ({
    patokanHarga,
    angsuran,
    totalAngsuran,
    jangkaWaktu,
    patokanBayangan,
    getMisalLelangKelompok,
  }) => {
    const patokanHargaNum = Number(patokanHarga) || 0;
    const angsuranNum = Number(angsuran) || 0;
    const totalAngsuranNum = Number(totalAngsuran) || 0;
    const jangkaWaktuNum = Number(jangkaWaktu) || 0;
    const patokanBayanganNum = Number(patokanBayangan) || 0;

    const bayangan = (totalAngsuranNum * patokanBayanganNum) / 100;

    const data = [];

    for (let i = 1; i <= jangkaWaktuNum; i++) {
      let potonganPersenAdmin = 0;
      let potonganAdmin = 0;
      let subtotal = 0;
      let limitMurni = 0;
      let limitLelang = 0;

      if (i >= 1 && i <= 12) {
        potonganPersenAdmin = 4.5;
        potonganAdmin = (patokanHargaNum * potonganPersenAdmin) / 100;
        subtotal = patokanHargaNum + potonganAdmin;

        limitMurni = subtotal - totalAngsuranNum;
        limitLelang = limitMurni + bayangan;
      } else if (i >= 13 && i <= 24) {
        potonganPersenAdmin = 4.25;
        potonganAdmin = (patokanHargaNum * potonganPersenAdmin) / 100;
        subtotal = patokanHargaNum + potonganAdmin;

        limitMurni = subtotal - totalAngsuranNum;
        limitLelang = limitMurni + bayangan;
      } else if (i >= 25 && i <= 26) {
        potonganPersenAdmin = 4;
        potonganAdmin = (patokanHargaNum * potonganPersenAdmin) / 100;
        subtotal = patokanHargaNum + potonganAdmin;

        limitMurni = subtotal - totalAngsuranNum;
        limitLelang = limitMurni + bayangan;
      } else {
        potonganPersenAdmin = 4;
        potonganAdmin = (patokanHargaNum * potonganPersenAdmin) / 100;

        let penurunan = 0;
        if (i === 27) penurunan = 15;
        else if (i === 28) penurunan = 24;
        else if (i === 29) penurunan = 33;
        else if (i === 30) penurunan = 42;
        else if (i === 31) penurunan = 47;
        else if (i === 32) penurunan = 53;
        else if (i === 33) penurunan = 58;
        else if (i === 34) penurunan = 65;
        else if (i === 35) penurunan = 73;
        else if (i === 36) penurunan = 80;

        potonganAdmin = potonganAdmin - (potonganAdmin * penurunan) / 100;
        subtotal = patokanHargaNum + potonganAdmin;

        limitMurni = subtotal - totalAngsuranNum;
        limitLelang = limitMurni + bayangan;
      }

      const hargaPatok = patokanHargaNum;
      let misalLelang = i >= 2 ? limitLelang : "";

      if (getMisalLelangKelompok.length !== 0) {
        const noLoop1 = String(i);
        const noLoop2 = String(i).padStart(2, "0");

        let findMisalLelangKelompok = getMisalLelangKelompok.find(
          (misalLelangKelompok) =>
            String(misalLelangKelompok.no) === noLoop1 ||
            String(misalLelangKelompok.no) === noLoop2,
        );

        if (findMisalLelangKelompok) {
          if (findMisalLelangKelompok.misalLelang > 0) {
            misalLelang = findMisalLelangKelompok.misalLelang;
          }
        }
      }

      const selisihHargaPatokDenganMisalLelang =
        i >= 2 ? hargaPatok - Number(misalLelang) : "";

      let hitPengembalianSdo = "";
      if (i >= 2 && i <= 27) {
        hitPengembalianSdo = Number(misalLelang);
      } else if (i >= 28) {
        hitPengembalianSdo = Number(limitLelang);
      }

      let limitLelangMurni = 0;
      if (i <= 27) {
        limitLelangMurni = limitMurni;
      } else {
        limitLelangMurni = data[i - 28]?.limitLelangMurni ?? limitMurni;
      }

      const totalHitPengembalianSdoDanLimitLelangMurni =
        i >= 2 ? Number(hitPengembalianSdo) - Number(limitLelangMurni) : "";

      let pembagian36 = "";
      if (i === 1) {
        pembagian36 =
          Number(totalHitPengembalianSdoDanLimitLelangMurni || 0) / 36;
      } else if (i === 2) {
        pembagian36 = "";
      } else if (i >= 3 && i <= 27) {
        pembagian36 =
          Number(totalHitPengembalianSdoDanLimitLelangMurni || 0) / 36;
      } else {
        pembagian36 = data[i - 29]?.pembagian36 ?? "";
      }

      let pgmblianSaldo10BlReal = "";
      if (i >= 28) {
        pgmblianSaldo10BlReal =
          Number(totalHitPengembalianSdoDanLimitLelangMurni || 0) / 36;
      }

      let pengembalianSaldo = 0;
      if (i === 1) {
        pengembalianSaldo = 0;
      } else {
        pengembalianSaldo = Number(pembagian36 || 0) * (i - 2 < 0 ? 0 : i - 2);
      }

      const terimaLelang =
        i === 1
          ? totalAngsuranNum
          : Number(selisihHargaPatokDenganMisalLelang) +
            Number(pengembalianSaldo);

      data.push({
        no: i,
        patokanHarga: patokanHargaNum,
        potonganAdmin,
        subtotal,
        totalAngsuran: totalAngsuranNum,
        limitMurni,
        bayangan,
        limitLelang,

        hargaPatok,
        misalLelang,
        selisihHargaPatokDenganMisalLelang,
        pengembalianSaldo,
        terimaLelang,
        hitPengembalianSdo,
        limitLelangMurni,
        totalHitPengembalianSdoDanLimitLelangMurni,
        pembagian36,
        pgmblianSaldo10BlReal,
      });
    }

    const hasilFinal = data.map((item, index) => {
      const bulanKe = index + 1;

      let pngmblanSaldoUtkPanitia = "";
      if (bulanKe === 1) {
        pngmblanSaldoUtkPanitia = ".";
      } else if (bulanKe === 2) {
        pngmblanSaldoUtkPanitia =
          Number(item.totalHitPengembalianSdoDanLimitLelangMurni || 0) / 36;
      } else if (bulanKe >= 3 && bulanKe <= 27) {
        pngmblanSaldoUtkPanitia = Number(item.pembagian36 || 0);
      } else {
        pngmblanSaldoUtkPanitia =
          data[bulanKe - 29]?.pngmblanSaldoUtkPanitia ?? "";
      }

      const tanpaNamaAtasSetoranBlnIni1 = angsuranNum;

      let tanpaNamaAtasSetoranBlnIni2 = "";
      if (bulanKe >= 2 && bulanKe <= 27) {
        tanpaNamaAtasSetoranBlnIni2 = Number(item.pembagian36 || 0);
      } else if (bulanKe >= 28) {
        tanpaNamaAtasSetoranBlnIni2 = data[bulanKe - 28]?.pembagian36 ?? "";
      }

      let setoranBlnIni = 0;
      if (bulanKe === 1) {
        setoranBlnIni = tanpaNamaAtasSetoranBlnIni1;
      } else {
        setoranBlnIni =
          Number(tanpaNamaAtasSetoranBlnIni1 || 0) -
          Number(tanpaNamaAtasSetoranBlnIni2 || 0);
      }

      return {
        ...item,
        pngmblanSaldoUtkPanitia,
        tanpaNamaAtasSetoranBlnIni1,
        tanpaNamaAtasSetoranBlnIni2,
        setoranBlnIni,
      };
    });

    const hasilFinal2 = hasilFinal.map((item, index) => {
      const bulanKe = index + 1;

      let tanpaNamaAtasTotalSetoranSdhMasuk = 0;
      if (bulanKe === 1) {
        tanpaNamaAtasTotalSetoranSdhMasuk = angsuranNum;
      } else if (bulanKe === 2) {
        tanpaNamaAtasTotalSetoranSdhMasuk = angsuranNum;
      } else {
        tanpaNamaAtasTotalSetoranSdhMasuk = Number(
          hasilFinal[index - 1]?.setUangMasukPerBln || 0,
        );
      }

      let totalSetoranSdhMasuk = 0;
      if (bulanKe === 1) {
        totalSetoranSdhMasuk = 0;
      } else {
        totalSetoranSdhMasuk = Number(item.setoranBlnIni || 0);
      }

      const setUangMasukPerBln =
        Number(tanpaNamaAtasTotalSetoranSdhMasuk || 0) +
        Number(totalSetoranSdhMasuk || 0);

      return {
        ...item,
        tanpaNamaAtasTotalSetoranSdhMasuk,
        totalSetoranSdhMasuk,
        setUangMasukPerBln,
      };
    });

    const hasilFinal3 = hasilFinal2.map((item, index) => {
      const bulanKe = index + 1;

      const tanpaNamaBawahSetUangMasuk1 = jangkaWaktuNum - bulanKe;

      const tanpaNamaBawahSetUangMasuk2 =
        Number(item.setUangMasukPerBln || 0) +
        Number(angsuranNum || 0) * Number(tanpaNamaBawahSetUangMasuk1 || 0);

      const tanpaNamaBawahSetUangMasuk3 =
        Number(item.terimaLelang || 0) -
        Number(tanpaNamaBawahSetUangMasuk2 || 0);

      return {
        ...item,
        tanpaNamaBawahSetUangMasuk1,
        tanpaNamaBawahSetUangMasuk2,
        tanpaNamaBawahSetUangMasuk3,
      };
    });

    return hasilFinal3.map((item) => roundObjectNumbers(item));
  };

  useEffect(() => {
    getKelompokById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getKelompokById = async () => {
    setLoading(true);

    try {
      const response = await postRequest(
        `/kelompoks/${id}`,
        {},
        navigate,
        dispatch,
      );

      setNoBukti(response.data.noKelompok);
      setTglKelompok(response.data.tglKelompok);
      setPatokanHarga(formatNumber(response.data.patokanHarga));
      setAngsuran(formatNumber(response.data.angsuran));
      setTotalAngsuran(formatNumber(response.data.totalAngsuran));
      setJangkaWaktu(roundInteger(response.data.jangkaWaktu));
      setAdministrasi(roundInteger(response.data.administrasi));
      setPatokanBayangan(roundInteger(response.data.patokanBayangan));
      setKeterangan(response.data.keterangan);
      setIsPost(response.data.isPost);

      const response2 = await postRequest(
        `/userPesertaKelompoksByKelompok`,
        { kelompokId: response.data.id, kodeCabang: user.cabang.id },
        navigate,
        dispatch,
      );
      setUserPesertaKelompoksData(response2.data);

      const response3 = await postRequest(
        `/pesertaKelompoksByPesertaKelompok`,
        { kelompokId: response.data.id, kodeCabang: user.cabang.id },
        navigate,
        dispatch,
      );
      setPesertaKelompoksData(response3.data);

      const response4 = await postRequest(
        `/angsuranKelompoksByKelompok`,
        { kelompokId: response.data.id, kodeCabang: user.cabang.id },
        navigate,
        dispatch,
      );
      setAngsuranKelompoksData(response4.data);

      const response5 = await postRequest(
        `/misalLelangKelompoksByKelompok`,
        { kelompokId: response.data.id, kodeCabang: user.cabang.id },
        navigate,
        dispatch,
      );
      setMisalLelangKelompoksData(response5.data);

      const response6 = await postRequest(
        `/biayaAngsuranKelompokByKelompok`,
        { kelompokId: response.data.id, kodeCabang: user.cabang.id },
        navigate,
        dispatch,
      );
      const total = response6.data.totalBiaya ?? 0;
      setTotalBiaya(formatNumber(total));

      const response7 = await postRequest(
        `/hitunganSimulasisByKelompok`,
        { kelompokId: response.data.id, kodeCabang: user.cabang.id },
        navigate,
        dispatch,
      );
      setHitunganSimulasisData(response7.data);

      const dataSimulasi = getSimulasiLimitLelang({
        patokanHarga: response.data.patokanHarga,
        angsuran: response.data.angsuran,
        totalAngsuran: response.data.totalAngsuran,
        jangkaWaktu: response.data.jangkaWaktu,
        patokanBayangan: response.data.patokanBayangan,
        getMisalLelangKelompok: response5.data,
      });

      setSimulasiLimitLelang(dataSimulasi);
    } catch (error) {
      alert(error?.response?.data?.message || String(error));
    }

    setLoading(false);
  };

  const deleteKelompok = async (id) => {
    setLoading(true);
    try {
      await postRequest(`/deleteKelompok/${id}`, {}, navigate, dispatch);
      navigate("/daftarKelompok");
    } catch (error) {
      alert(error?.response?.data?.message || String(error));
    }
    setLoading(false);
  };

  const handleDownloadUser = () => {
    const header = ["Username", "Password"];
    const body = (userPesertaKelompoksData || []).map((u) => [
      u?.username ?? "",
      u?.password ?? "",
    ]);

    downloadExcel({
      fileName: "Daftar User Peserta",
      sheet: "DaftarUserPeserta",
      tablePayload: {
        header,
        body,
      },
    });
  };

  const handleDownloadSimulasi = () => {
    const jw = Number(jangkaWaktu) || 0;
    const arr = simulasiLimitLelang || [];

    const header = [
      "",
      ...Array.from({ length: jw }, (_, i) => `Bulan Ke - ${i + 1}`),
    ];

    const row = (label, keyOrFn, isFormatted = false) => {
      const cells = Array.from({ length: jw }, (_, i) => {
        const item = arr[i];
        if (!item) return "";
        const value =
          typeof keyOrFn === "function" ? keyOrFn(item, i) : item?.[keyOrFn];
        return isFormatted ? formatNumber(value) : (value ?? "");
      });
      return [label, ...cells];
    };

    const body = [
      header,
      row("Harga Patokan", "patokanHarga", true),
      row("4.5%", "potonganAdmin", true),
      row("", "subtotal", true),
      row("Setoran", "totalAngsuran", true),
      row("Limit Murni", "limitMurni", true),
      row(`Bayangan ${patokanBayangan}%`, "bayangan", true),
      row("Limit Lelang", "limitLelang", true),

      row("Harga Patok", "hargaPatok", true),
      row("Misal Lelang", "misalLelang", true),
      row("", "selisihHargaPatokDenganMisalLelang", true),
      row("Pengembalian saldo", "pengembalianSaldo", true),
      row("Terima lelang", "terimaLelang", true),
      row("Hit. Pengembalian sdo", "hitPengembalianSdo", true),
      row("Limit lelang murni", "limitLelangMurni", true),
      row("", "totalHitPengembalianSdoDanLimitLelangMurni", true),
      row("", "pembagian36", true),

      row("pgmblian saldo 10 bl real", "pgmblianSaldo10BlReal", true),
      row("Pngmblan saldo utk panitia", "pngmblanSaldoUtkPanitia", true),
      row("", "tanpaNamaAtasSetoranBlnIni1", true),
      row("", "tanpaNamaAtasSetoranBlnIni2", true),
      row("Setoran bln ini ====>", "setoranBlnIni", true),
      row("", "tanpaNamaAtasTotalSetoranSdhMasuk", true),
      row("total setoran sdh masuk ", "totalSetoranSdhMasuk", true),
      row("Set Uang masuk / bln", "setUangMasukPerBln", true),
      row("", "tanpaNamaBawahSetUangMasuk1", true),
      row("", "tanpaNamaBawahSetUangMasuk2", true),
      row("", "tanpaNamaBawahSetUangMasuk3", true),
    ];

    downloadExcel({
      fileName: "Simulasi Limit Lelang",
      sheet: "SimulasiLimitLelang",
      tablePayload: {
        header: [],
        body,
      },
    });
  };

  const handleDownloadHitungSimulasi = () => {
    const header = [
      "No.",
      "Nama Peserts",
      "Tgl. Lelang",
      "Nominal Lelang",
      "Subsidi",
      "Angsuran",
      "Cadangan",
      "Total Penerimaan Lelang",
      "Lelang Undi",
      "Sisa Periode",
      "Hutang",
      "Yang Sudah Disetor",
      "Total Setor",
      "Uang Yang Diterima",
      "Selisih",
      "Bunga",
      "Telat Bulan",
      "Nominal Terlambat",
      "Adm 1",
      "Adm 2",
      "Adm 3",
      "Patokan Harga",
      "Pendapatan Adm",
    ];

    const body = calculatedHitunganSimulasisData.map((u, i) => [
      i + 1,
      pesertaKelompoksData[i]
        ? pesertaKelompoksData[i].peserta.namaPeserta
        : "",
      u.tglLelang && formatDate(u.tglLelang),
      formatNumber(u.nominalLelang),
      formatNumber(u.subsidi),
      formatNumber(u.angsuran),
      formatNumber(u.cadangan),
      formatNumber(u.totalPenerimaanLelang),
      u.lelangUndi,
      formatNumber(u.sisaPeriode),
      formatNumber(u.hutang),
      formatNumber(u.yangSudahDisetor),
      formatNumber(u.totalSetor),
      formatNumber(u.uangYangDiterima),
      formatNumber(u.selisih),
      formatBungaPercent(u.bunga),
      formatNumber(u.telatBulan),
      formatNumber(u.nominalTerlambat),
      formatPercent(u.adm1, 2),
      formatPercent(u.adm2, 2),
      formatPercent(u.adm3, 2),
      formatNumber(u.patokanHarga),
      formatNumber(u.pendapatanAdm),
    ]);

    downloadExcel({
      fileName: "Hitung Simulasi",
      sheet: "HitungSimulasi",
      tablePayload: {
        header,
        body,
      },
    });
  };

  if (loading) return <Loader />;

  const textRight = {
    textAlign: screenSize >= 650 && "right",
  };

  return (
    <Container>
      <h3>Arisan</h3>
      <h5 style={{ fontWeight: 400 }}>Kelompok</h5>
      <hr />

      <Button
        variant="outlined"
        color="secondary"
        onClick={() => navigate("/daftarKelompok")}
        sx={{ marginLeft: 2, marginTop: 4 }}
      >
        {"< Kembali"}
      </Button>

      <Box sx={downloadButtons}>
        <ButtonGroup variant="outlined" color="secondary">
          <Button
            color="secondary"
            startIcon={<SearchIcon />}
            onClick={() => {
              setPreviewExcel(!previewExcel);
              setPreviewExcelSimulasiLimitLelang(false);
              setPreviewExcelHitungSimulasi(false);
            }}
          >
            Excel
          </Button>
          <Button
            color="primary"
            onClick={() => {
              setPreviewExcel(false);
              setPreviewExcelSimulasiLimitLelang(
                !previewExcelSimulasiLimitLelang,
              );
              setPreviewExcelHitungSimulasi(false);
            }}
          >
            Simulasi Limit Lelang
          </Button>
          <Button
            color="primary"
            onClick={() => {
              setPreviewExcel(false);
              setPreviewExcelSimulasiLimitLelang(false);
              setPreviewExcelHitungSimulasi(!previewExcelHitungSimulasi);
            }}
          >
            Hitung Simulasi
          </Button>
        </ButtonGroup>
      </Box>

      <div>
        {previewExcel && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadUser}
          >
            EXCEL
          </Button>
        )}

        <table
          className="styled-table"
          id="table-user"
          style={{ fontSize: "10px" }}
        >
          {previewExcel && (
            <>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Password</th>
                </tr>
              </thead>
              <tbody>
                {userPesertaKelompoksData.map((u) => (
                  <tr key={u.id}>
                    <td>{u.username}</td>
                    <td>{u.password}</td>
                  </tr>
                ))}
              </tbody>
            </>
          )}
        </table>
      </div>

      <div>
        {previewExcelSimulasiLimitLelang && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadSimulasi}
          >
            EXCEL
          </Button>
        )}

        <table
          className="styled-table"
          id="table-simulasi"
          style={{ fontSize: "10px" }}
        >
          {previewExcelSimulasiLimitLelang && (
            <tbody>
              <tr>
                <td></td>
                {Array.from({ length: Number(jangkaWaktu) || 0 }, (_, i) => (
                  <td key={i}>Bulan Ke - {i + 1}</td>
                ))}
              </tr>

              <tr>
                <td>Harga Patokan</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.patokanHarga)}</td>
                ))}
              </tr>

              <tr>
                <td>4.5%</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.potonganAdmin)}</td>
                ))}
              </tr>

              <tr>
                <td></td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.subtotal)}</td>
                ))}
              </tr>

              <tr>
                <td>Setoran</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.totalAngsuran)}</td>
                ))}
              </tr>

              <tr>
                <td>Limit Murni</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.limitMurni)}</td>
                ))}
              </tr>

              <tr>
                <td>Bayangan {patokanBayangan}%</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.bayangan)}</td>
                ))}
              </tr>

              <tr>
                <td>Limit Lelang</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.limitLelang)}</td>
                ))}
              </tr>

              <tr>
                <td>Harga Patok</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.hargaPatok)}</td>
                ))}
              </tr>

              <tr>
                <td>Misal Lelang</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.misalLelang)}</td>
                ))}
              </tr>

              <tr>
                <td></td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>
                    {formatNumber(x.selisihHargaPatokDenganMisalLelang)}
                  </td>
                ))}
              </tr>

              <tr>
                <td>Pengembalian saldo</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.pengembalianSaldo)}</td>
                ))}
              </tr>

              <tr>
                <td>Terima lelang</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.terimaLelang)}</td>
                ))}
              </tr>

              <tr>
                <td>Hit. Pengembalian sdo</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.hitPengembalianSdo)}</td>
                ))}
              </tr>

              <tr>
                <td>Limit lelang murni</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.limitLelangMurni)}</td>
                ))}
              </tr>

              <tr>
                <td></td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>
                    {formatNumber(x.totalHitPengembalianSdoDanLimitLelangMurni)}
                  </td>
                ))}
              </tr>

              <tr>
                <td></td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.pembagian36)}</td>
                ))}
              </tr>

              <tr>
                <td>pgmblian saldo 10 bl real</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.pgmblianSaldo10BlReal)}</td>
                ))}
              </tr>

              <tr>
                <td>Pngmblan saldo utk panitia</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.pngmblanSaldoUtkPanitia)}</td>
                ))}
              </tr>

              <tr>
                <td></td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>
                    {formatNumber(x.tanpaNamaAtasSetoranBlnIni1)}
                  </td>
                ))}
              </tr>

              <tr>
                <td></td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>
                    {formatNumber(x.tanpaNamaAtasSetoranBlnIni2)}
                  </td>
                ))}
              </tr>

              <tr>
                <td>Setoran bln ini ====&gt;</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.setoranBlnIni)}</td>
                ))}
              </tr>

              <tr>
                <td></td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>
                    {formatNumber(x.tanpaNamaAtasTotalSetoranSdhMasuk)}
                  </td>
                ))}
              </tr>

              <tr>
                <td>total setoran sdh masuk </td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.totalSetoranSdhMasuk)}</td>
                ))}
              </tr>

              <tr>
                <td>Set Uang masuk / bln</td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>{formatNumber(x.setUangMasukPerBln)}</td>
                ))}
              </tr>

              <tr>
                <td></td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>
                    {formatNumber(x.tanpaNamaBawahSetUangMasuk1)}
                  </td>
                ))}
              </tr>

              <tr>
                <td></td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>
                    {formatNumber(x.tanpaNamaBawahSetUangMasuk2)}
                  </td>
                ))}
              </tr>

              <tr>
                <td></td>
                {simulasiLimitLelang.map((x, idx) => (
                  <td key={idx}>
                    {formatNumber(x.tanpaNamaBawahSetUangMasuk3)}
                  </td>
                ))}
              </tr>
            </tbody>
          )}
        </table>
      </div>

      <div>
        {previewExcelHitungSimulasi && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadHitungSimulasi}
          >
            EXCEL
          </Button>
        )}

        <table
          className="styled-table"
          id="table-simulasi"
          style={{ fontSize: "10px" }}
        >
          {previewExcelHitungSimulasi && (
            <>
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Nama Peserts</th>
                  <th>Tgl. Lelang</th>
                  <th>Nominal Lelang</th>
                  <th>Subsidi</th>
                  <th>Angsuran</th>
                  <th>Cadangan</th>
                  <th>Total Penerimaan Lelang</th>
                  <th>Lelang Undi</th>
                  <th>Sisa Periode</th>
                  <th>Hutang</th>
                  <th>Yang Sudah Disetor</th>
                  <th>Total Setor</th>
                  <th>Uang Yang Diterima</th>
                  <th>Selisih</th>
                  <th>Bunga</th>
                  <th>Telat Bulan</th>
                  <th>Nominal Terlambat</th>
                  <th>Adm 1</th>
                  <th>Adm 2</th>
                  <th>Adm 3</th>
                  <th>Patokan Harga</th>
                  <th>Pendapatan Adm</th>
                </tr>
              </thead>
              <tbody>
                {calculatedHitunganSimulasisData.map((u, i) => (
                  <tr key={u.id || i}>
                    <td>{i + 1}</td>
                    <td>
                      {pesertaKelompoksData[i]
                        ? pesertaKelompoksData[i].peserta.namaPeserta
                        : ""}
                    </td>
                    <td>{u.tglLelang && formatDate(u.tglLelang)}</td>
                    <td>{formatNumber(u.nominalLelang)}</td>
                    <td>{formatNumber(u.subsidi)}</td>
                    <td>{formatNumber(u.angsuran)}</td>
                    <td>{formatNumber(u.cadangan)}</td>
                    <td>{formatNumber(u.totalPenerimaanLelang)}</td>
                    <td>{u.lelangUndi}</td>
                    <td>{formatNumber(u.sisaPeriode)}</td>
                    <td>{formatNumber(u.hutang)}</td>
                    <td>{formatNumber(u.yangSudahDisetor)}</td>
                    <td>{formatNumber(u.totalSetor)}</td>
                    <td>{formatNumber(u.uangYangDiterima)}</td>
                    <td>{formatNumber(u.selisih)}</td>
                    <td>{formatBungaPercent(u.bunga)}</td>
                    <td>{formatNumber(u.telatBulan)}</td>
                    <td>{formatNumber(u.nominalTerlambat)}</td>
                    <td>{formatPercent(u.adm1, 2)}</td>
                    <td>{formatPercent(u.adm2, 2)}</td>
                    <td>{formatPercent(u.adm3, 2)}</td>
                    <td>{formatNumber(u.patokanHarga)}</td>
                    <td>{formatNumber(u.pendapatanAdm)}</td>
                  </tr>
                ))}
              </tbody>
            </>
          )}
        </table>
      </div>

      <Box sx={buttonModifierContainer}>
        {isEditable && (
          <ButtonModifier
            id={id}
            kode={"test"}
            addLink={`/daftarKelompok/kelompok/${id}/tambahPesertaKelompok`}
            editLink={`/daftarKelompok/kelompok/${id}/edit`}
            deleteUser={deleteKelompok}
            nameUser={noKelompok}
          />
        )}
      </Box>

      <Form>
        <Card>
          <Card.Header>Kelompok</Card.Header>
          <Card.Body>
            <Box sx={textFieldContainer}>
              <Box sx={textFieldWrapper}>
                <Row>
                  <Col>
                    <Form.Group
                      as={Row}
                      className="mb-3"
                      controlId="noKelompok"
                    >
                      <Form.Label column sm="5" style={textRight}>
                        No. Kelompok :
                      </Form.Label>
                      <Col sm="7">
                        <Form.Control
                          required
                          value={noKelompok}
                          disabled
                          readOnly
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group
                      as={Row}
                      className="mb-3"
                      controlId="tglKelompok"
                    >
                      <Form.Label column sm="5" style={textRight}>
                        Tgl. Kelompok :
                      </Form.Label>
                      <Col sm="7">
                        <Form.Control
                          required
                          value={tglKelompok}
                          disabled
                          readOnly
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group
                      as={Row}
                      className="mb-3"
                      controlId="patokanHarga"
                    >
                      <Form.Label column sm="5" style={textRight}>
                        Patokan Harga :
                      </Form.Label>
                      <Col sm="7">
                        <Form.Control
                          required
                          value={patokanHarga}
                          disabled
                          readOnly
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group as={Row} className="mb-3" controlId="angsuran">
                      <Form.Label column sm="5" style={textRight}>
                        Angsuran :
                      </Form.Label>
                      <Col sm="7">
                        <Form.Control
                          required
                          value={angsuran}
                          disabled
                          readOnly
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group
                      as={Row}
                      className="mb-3"
                      controlId="jangkaWaktu"
                    >
                      <Form.Label column sm="5" style={textRight}>
                        Jangka Waktu :
                      </Form.Label>
                      <Col sm="7">
                        <Form.Control
                          required
                          value={jangkaWaktu}
                          disabled
                          readOnly
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group
                      as={Row}
                      className="mb-3"
                      controlId="totalAngsuran"
                    >
                      <Form.Label column sm="5" style={textRight}>
                        Total Angsuran :
                      </Form.Label>
                      <Col sm="7">
                        <Form.Control
                          required
                          value={totalAngsuran}
                          disabled
                          readOnly
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group
                      as={Row}
                      className="mb-3"
                      controlId="administrasi"
                    >
                      <Form.Label column sm="5" style={textRight}>
                        Administrasi (%) :
                      </Form.Label>
                      <Col sm="7">
                        <Form.Control
                          required
                          value={administrasi}
                          disabled
                          readOnly
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group
                      as={Row}
                      className="mb-3"
                      controlId="patokanBayangan"
                    >
                      <Form.Label column sm="5" style={textRight}>
                        Patokan Bayangan (%) :
                      </Form.Label>
                      <Col sm="7">
                        <Form.Control
                          required
                          value={patokanBayangan}
                          disabled
                          readOnly
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group
                      as={Row}
                      className="mb-3"
                      controlId="keterangan"
                    >
                      <Form.Label column sm="5" style={textRight}>
                        Keterangan :
                      </Form.Label>
                      <Col sm="7">
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={keterangan}
                          disabled
                          readOnly
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              </Box>

              <Box sx={[textFieldWrapper, secondWrapper]}>
                <Row>
                  <Col>
                    <Form.Group
                      as={Row}
                      className="mb-3"
                      controlId="totalBiaya"
                    >
                      <Form.Label column sm="5" style={textRight}>
                        Total Biaya :
                      </Form.Label>
                      <Col sm="7">
                        <Form.Control
                          required
                          value={totalBiaya}
                          disabled
                          readOnly
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              </Box>
            </Box>
          </Card.Body>
        </Card>
      </Form>

      <Card style={{ marginTop: 10 }}>
        <Card.Header>Daftar Angsuran</Card.Header>
        <Card.Body>
          <Box sx={tableContainer}>
            <ShowTableAngsuranKelompok
              id={id}
              currentPosts={currentPostsAngsuranKelompok}
            />
          </Box>
          <Box sx={tableContainer}>
            <Pagination
              count={countAngsuranKelompok}
              page={pageAngsuranKelompok}
              onChange={handleChangeAngsuranKelompok}
              color="primary"
              size={screenSize <= 600 ? "small" : "large"}
            />
          </Box>
        </Card.Body>
      </Card>

      <Card style={{ marginTop: 10 }}>
        <Card.Header>Daftar Anggota</Card.Header>
        <Card.Body>
          <Box sx={tableContainer}>
            <ShowTablePesertaKelompok id={id} currentPosts={currentPosts} />
          </Box>
          <Box sx={tableContainer}>
            <Pagination
              count={count}
              page={page}
              onChange={handleChange}
              color="primary"
              size={screenSize <= 600 ? "small" : "large"}
            />
          </Box>
        </Card.Body>
      </Card>

      <Card style={{ marginTop: 10 }}>
        <Card.Header>Hitungan Simulasi</Card.Header>
        <Card.Body>
          <Box sx={tableContainer}>
            <ShowTableHitunganSimulasi
              id={id}
              currentPosts={currentPostsHitunganSimulasi}
            />
          </Box>
          <Box sx={tableContainer}>
            <Pagination
              count={countHitunganSimulasi}
              page={pageHitunganSimulasi}
              onChange={handleChangeHitunganSimulasi}
              color="primary"
              size={screenSize <= 600 ? "small" : "large"}
            />
          </Box>
        </Card.Body>
      </Card>

      <Card style={{ marginTop: 10 }}>
        <Card.Header>Misal Lelang</Card.Header>
        <Card.Body>
          <Box sx={tableContainer}>
            <ShowTableMisalLelangKelompok
              id={id}
              currentPosts={currentPostsMisalLelangKelompok}
            />
          </Box>
          <Box sx={tableContainer}>
            <Pagination
              count={countMisalLelangKelompok}
              page={pageMisalLelangKelompok}
              onChange={handleChangeMisalLelangKelompok}
              color="primary"
              size={screenSize <= 600 ? "small" : "large"}
            />
          </Box>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default TampilKelompok;

const buttonModifierContainer = {
  mt: 4,
  mb: 4,
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
};

const tableContainer = {
  pt: 4,
  display: "flex",
  justifyContent: "center",
};

const downloadButtons = {
  mt: 4,
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
};

const textFieldContainer = {
  display: "flex",
  flexDirection: {
    xs: "column",
    sm: "row",
  },
};

const textFieldWrapper = {
  display: "flex",
  flex: 1,
  flexDirection: "column",
  maxWidth: {
    md: "40vw",
  },
};

const secondWrapper = {
  marginLeft: {
    sm: 4,
  },
  marginTop: {
    sm: 0,
    xs: 4,
  },
};
